import { MatchDetail } from "@app/core/match/domain/model/match-detail";
import { StatisticsMapper } from "@app/core/statistics/data/network/mapper/statistics.mapper";
import { DateMapper } from "@app/core/shared/data/network/date.mapper";
import { BoxItem, ListItem, Section } from "../../domain/model/section";
import fakeSections from "./match-detail-sections-v2.fake";
import { sections as fakeSectionsV1 } from "./match-detail-sections.fake";

class SectionsMapper {

  private static deserializeListSection(section: any): Section {
    let items: ListItem[] = [];
    for (const key in section.subsections) {
      const subsection = section.subsections[key];
      items.push({
        type: section.section_type,
        title: subsection.subsection_title,
        list: subsection.subsection_content.text_content ? [subsection.subsection_content.text_content] : subsection.subsection_content.list_items
      });
    }

    return {
      section_id: section.section_type,
      section_title: section.section_title,
      section_info: section.section_info,
      stat_type: 'list',
      list_items: items
    };
  }

  private static deserializeBoxSection(section: any): Section {
    let items: BoxItem[] = [];
    for (const key in section.subsections) {
      const subsection = section.subsections[key];
      items.push({
        title: subsection.subsection_title,
        group: subsection.subsection_group,
        info: subsection.subsection_info,
        color: subsection.subsection_color,
        stat: subsection.subsection_content,
      });
    }

    return {
      section_id: section.section_type,
      section_title: section.section_title,
      section_info: section.section_info,
      stat_type: "box",
      box_items: items,
    };
  }

  private static deserializeHeatmapSection(section: any): Section {
    return {
      section_id: section.section_type,
      section_title: section.subsections[0].subsection_title,
      section_info: section.section_info,
      stat_type: 'heatmap',
      heatmap: {
        url: section.subsections[0].subsection_content.image_url,
        legend: section.subsections[1].subsection_content.list_items.length > 0 ? section.subsections[1].subsection_content.list_items : undefined
      }
    };
  }

  private static deserializeTableSection(section: any): Section {
    return {
      section_id: section.section_type,
      section_title: section.section_title,
      section_subtitle: section.subsections[0].subsection_title,
      section_info: section.subsections[0].subsection_info,
      stat_type: 'table',
      table: {
        data: section.subsections[0].subsection_content.table_content,
        legend: section.subsections[1].subsection_content.html
      }
    };
  }

  private static deserializeRadarChartSection(section: any): Section {
    return {
      section_id: section.section_type,
      section_title: section.section_title,
      section_subtitle: section.subsections[0].subsection_content.stat_content[0].title,
      section_info: section.subsections[0].subsection_content.stat_content[0].description,
      stat_type: 'radarchart',
      radarchart: {
        stat: section.subsections[0].subsection_content.stat_content[0].stat,
        names: section.subsections[0].subsection_content.stat_content[0].value_names,
        xlimit: section.subsections[0].subsection_content.stat_content[0].x_axis_limit
      }
    };
  }

  private static deserializeVideoHighlightSection(section: any): Section {
    const videos: Section['videos'] = section.subsections.map((subsection: any) => ({
      url: subsection.subsection_content.video_url,
      download_url: subsection.subsection_content.video_url_download,
      thumbnail: subsection.subsection_content.thumbnail_url,
      title: subsection.subsection_title,
      analytics_indentifier: subsection.subsection_content.analytics_indentifier,
    }));

    return {
      section_id: section.section_id,
      section_title: section.section_title,
      section_info: section.section_info,
      stat_type: "vertical-video",
      videos
    };
  }

  private static deserializeVideoSection(section: any, sections: Section[], videosIndex: number | undefined): number | undefined {
    if (videosIndex === undefined) {
      sections.push({
        section_id: section.section_type,
        section_title: "video-recordings",
        section_info: section.section_info,
        stat_type: "video",
        videos: [
          {
            url: section.subsections[0].subsection_content.video_url,
            download_url: section.subsections[0].subsection_content.video_url_download,
            title: section.section_title,
            analytics_indentifier:
              section.subsections[0].subsection_content.analytics_indentifier,
            loading: {
              loading: section.subsections[0].subsection_is_loading,
              loading_id: section.subsections[0].subsection_loading_id,
              check_every_seconds:
                section.subsections[0].subsection_loading_check_every,
            },
          },
        ],
      });
    return sections.length - 1;
  } else {
      sections[videosIndex].videos!.push({
        url: section.subsections[0].subsection_content.video_url,
        download_url: section.subsections[0].subsection_content.video_url_download,
        title: section.section_title,
        analytics_indentifier: section.subsections[0].subsection_content.analytics_indentifier,
        loading: {
          loading: section.subsections[0].subsection_is_loading,
          loading_id: section.subsections[0].subsection_loading_id,
          check_every_seconds: section.subsections[0].subsection_loading_check_every
        }
      });
      return videosIndex;
    }
  }

  private static deserializeComplexSection(section: any, sections: Section[], tabsIndex: number | undefined): number | undefined {
    let items: Section[] = [];
    let positionIndividualIndex: number | undefined = undefined;
    let positionTeamIndex: number | undefined = undefined;
    let positionOponentIndex: number | undefined = undefined;
    let servesSimpleIndex: number | undefined = undefined;
    let restosSimpleIndex: number | undefined = undefined;

    for (const key in section.subsections) {
      const subsection = section.subsections[key];
      if (subsection.subsection_content.type === 'stat') {
        const aux = this.orderSimpleData(subsection.subsection_content.stat_content);

        if (
          [
            "basculacio_vertical_individual",
            "pct_basc_vertical",
            "pct_basc_vertical_rival",
            "pct_primeros_saques",
            "pct_error_resto",
          ].includes(aux[0].id)
        ) {
          if (aux[0].id === "pct_primeros_saques") {
            items.push({
              section_id: aux[0].id,
              stat_type: "simple-data",
              simple_data: [{ ...aux[0].stat, info: aux[0].description, cols: 6, xl_cols: 12, truncate: true }],
            });
            servesSimpleIndex = items.length - 1;
          } else if (aux[0].id === "pct_error_resto") {
            items.push({
              section_id: aux[0].id,
              stat_type: "simple-data",
              simple_data: [{ ...aux[0].stat, info: aux[0].description, cols: 6, xl_cols: 12, truncate: true }],
            });
            restosSimpleIndex = items.length - 1;
          } else {
            items.push({
              section_id: aux[0].id,
              section_title: aux[0].title,
              section_info: aux[0].description,
              stat_type: "boxdata",
              boxdata: [{ ...aux[0].stat, progressbar: true }],
            });
          }

          if (aux[0].id === "basculacio_vertical_individual")
            positionIndividualIndex = items.length - 1;
          else if (aux[0].id === "pct_basc_vertical")
            positionTeamIndex = items.length - 1;
          else if (aux[0].id === "pct_basc_vertical_rival")
            positionOponentIndex = items.length - 1;
        } else if (
          [
            "basculacio_horitzontal_individual",
            "pct_basc_hor",
            "pct_basc_hor_rival",
            "velocidadsaques",
            "pct_winner_serve",
          ].includes(aux[0].id)
        ) {
          if (aux[0].id === "basculacio_horitzontal_individual") {
            items[positionIndividualIndex!].boxdata!.push({
              ...aux[0].stat,
              progressbar: true,
            });
          } else if (aux[0].id === "pct_basc_hor") {
            items[positionTeamIndex!].boxdata!.push({
              ...aux[0].stat,
              progressbar: true,
            });
          } else if (aux[0].id === "pct_basc_hor_rival") {
            items[positionOponentIndex!].boxdata!.push({
              ...aux[0].stat,
              progressbar: true,
            });
          } else if (aux[0].id === "velocidadsaques") {
            items[servesSimpleIndex!].simple_data!.push({
              ...aux[0].stat,
              name: aux[0].title,
              info: aux[0].description,
              cols: 6,
            });
          } else if (aux[0].id === "pct_winner_serve") {
            items[restosSimpleIndex!].simple_data!.push({
              ...aux[0].stat,
              name: aux[0].title,
              info: aux[0].description,
              cols: 6,
              truncate: true,
            });
          }
        } else if (
          ["velo_hits", "golpes", "golpes_equipo_rival"].includes(aux[0].id)
        ) {
          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "boxdata",
            boxdata: aux[0].stat,
          });
        } else if (aux[0].id === "restos") {
          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "piechart",
            piechart: aux[0].stat,
          });
        } else if (aux[0].id === "calidad_globos") {
          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "matrix",
            matrix: aux[0].stat,
          });
        } else if (aux[0].type === "piechart") {
          // Check if all stat elements have the same color
          if (Array.isArray(aux[0].stat) && aux[0].stat.length > 0) {
            const firstColor = aux[0].stat[0].color;
            const allSameColor = aux[0].stat.every((item: any) => {
              return item.color === firstColor;
            });
            if (allSameColor) {
              const newColors = ["#408FA2", "#52AFD0", "#e5e5e5"];
              aux[0].stat = aux[0].stat.map((item: any, idx: number) => ({
                ...item,
                color: newColors[idx] || item.color,
              }));
            }
          }

          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "piechart",
            piechart: aux[0].stat,
            section_short_title: aux[0].inside_title,
          });
        } else if (aux[0].type === "progressbar") {
          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "progressbar",
            progressbar: aux[0].stat,
          });
        } else if (aux[0].type === "multiple-progressbar") {
          items.push({
            section_id: aux[0].id,
            section_title: aux[0].title,
            section_info: aux[0].description,
            stat_type: "multiple-progressbar",
            multiple_progressbar: aux[0].stat,
            section_short_title: aux[0].inside_title,
            min_value: aux[0].min_value || 0,
            max_value: aux[0].max_value || 100,
          });
        } else if (aux[0].type === "numeric") {
          // TODO: This is a hacky solution to display numeric stats as simple-data boxes.
          // The 'numeric' type from the backend is converted to 'simple-data' with cols: 6
          // to match the styling of other stats (like in the Serveis section).
          // We remove the section_title to avoid redundancy with the stat's own name field.
          // Consider creating a dedicated 'numeric' stat_type and component for better semantics.
          items.push({
            section_id: aux[0].id,
            section_info: aux[0].description,
            stat_type: "simple-data-large",
            simple_data: [
              {
                ...aux[0].stat,
                info: aux[0].description,
                name: aux[0].title,
                truncate: true,
              },
            ],
          });
        }
      } else if (subsection.subsection_content.type === 'html') {
        items.push({
          section_id: subsection.subsection_id,
          section_title: subsection.subsection_title,
          section_info: subsection.subsection_info,
          html: subsection.subsection_content.html,
          stat_type: 'html'
        });
      }
    }

    if (tabsIndex === undefined) {
      sections.push({
        section_id: 'performance-detailed',
        stat_type: 'tabs',
        section_title: 'stats.performance-detailed',
        tabs: [{
          id: section.section_type,
          title: section.section_type,
          stats: items
        }]
      });
      return sections.length - 1;
    } else {
      sections[tabsIndex].tabs?.push({
        id: section.section_type,
        title: section.section_type,
        stats: items
      });
      return tabsIndex;
    }
  }

  private static orderSimpleData(data: any[]): any[] {
    return data.map((item) => ({
      ...item,
      stat: Array.isArray(item.stat)
      ? item.stat.sort((a: any, b: any) => a.value_orden - b.value_orden)
      : item.stat,
    }));
  }

  static deserialize(response: any): Section[] {

    let sections: Section[] = []
    let videosIndex: number | undefined = undefined
    let tabsIndex: number | undefined = undefined

    for (const key in response) {
      const section = response[key];
      switch (section.section_type) {
        case "strengths":
        case "improvement":
        case "objectives":
          sections.push(this.deserializeListSection(section));
          break;
        case "box_list":
          sections.push(this.deserializeBoxSection(section));
          break;
        case "comparative-heat":
          sections.push(this.deserializeHeatmapSection(section));
          break;
        case "comparative-table":
          sections.push(this.deserializeTableSection(section));
          break;
        case "comparative-radar":
          sections.push(this.deserializeRadarChartSection(section));
          break;
        case "video":
          videosIndex = this.deserializeVideoSection(
            section,
            sections,
            videosIndex
          );
          break;
        case "vertical_videos":
          sections.push(this.deserializeVideoHighlightSection(section));
          break;
        case "hits":
        case "positioning":
        case "serves":
        case "contribution":
          tabsIndex = this.deserializeComplexSection(
            section,
            sections,
            tabsIndex
          );
          break;
      }
    }

    return sections
  }
    
}

export class MatchDetailMapper {
  static deserialize(response: any): MatchDetail {
    const sections = SectionsMapper.deserialize(response.sections);
    const sections_v2 = response.sections_v2
    //console.log("Deserialized sections:", sections); // Debug log to check the deserialized sections
    //console.log("Sections_v2 from response:", sections_v2); // Debug log to check the original sections_v2 from the response
    //console.log("Fake sections:", fakeSections); // Debug log to check the fake sections  
    const victory =
      response.victory === 1
        ? "victory"
        : response.victory === 0
          ? "defeat"
          : null;
    return {
      code: response.code,
      club: response.club,
      court: response.pista,
      location: response.location,
      date: DateMapper.deserialize(response.date),
      durationMinutes: response.duration_minutes,
      funFacts: response.fun_fact,
      urlVideo: response.url_video,
      urlVideoExtended: response.url_video_extended,
      statistics: StatisticsMapper.deserialize(response.statistics),
      recommendation: response.recomendation,
      infographic: response.infographic,
      victory: victory,
      sections: sections,
      sections_v2: response.sections_v2,
      url_payment: response.url_payment,
      payment_done: response.payment_done,
      shareable: response.shareable,
      players: response.players,
      shared_by: response.shared_by,
      display_text: response.display_text,
      customName: response.custom_name,
    };
  }
}
